import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  QueryStateBoundary,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import {
  useCancelPractitionerBooking,
  useCompletePractitionerBooking,
  useConfirmPractitionerBooking,
  useCreateCommitment,
  useGeneratePractitionerInvoice,
  usePractitioner,
  usePractitionerBookings,
  usePractitionerCommitments,
} from "@/api/hooks/usePractitioners";
import {
  COI_STATUS_BADGE_VARIANT,
  COI_STATUS_LABELS,
  COMMITMENT_STATUS_BADGE_VARIANT,
  PAYMENT_LANE_LABELS,
  type PractitionerBooking,
  type PractitionerCommitment,
} from "@/api/types/practitioner";
import { BOOKING_STATUS_BADGE_VARIANT, BOOKING_STATUS_LABELS } from "@/api/types/booking";
import { PractitionerFormDialog } from "./PractitionerFormDialog";
import { BookPractitionerSessionDialog } from "./BookPractitionerSessionDialog";

export function PractitionerDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: practitioner, isLoading, error, refetch } = usePractitioner(id);
  const { data: commitments } = usePractitionerCommitments(id);
  const { data: bookings } = usePractitionerBookings(id);

  const [editOpen, setEditOpen] = useState(false);
  const [commitmentOpen, setCommitmentOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  return (
    <div className="p-6 sm:p-8">
      <Link to="/practitioners" className="mb-4 inline-block text-sm text-text-secondary hover:underline">
        ← Back to practitioners
      </Link>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {practitioner && (
          <>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="font-display text-2xl font-bold text-text-primary">{practitioner.contact_name}</h1>
                {practitioner.business_name && (
                  <p className="mt-1 text-sm text-text-secondary">{practitioner.business_name}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={COI_STATUS_BADGE_VARIANT[practitioner.coi_status]}>
                  {COI_STATUS_LABELS[practitioner.coi_status]}
                </Badge>
                {practitioner.coi_expiry_date && (
                  <span className="text-xs text-text-muted">expires {practitioner.coi_expiry_date}</span>
                )}
                <Badge variant="secondary">{PAYMENT_LANE_LABELS[practitioner.payment_lane]}</Badge>
                <Badge variant={practitioner.active ? "success" : "default"}>
                  {practitioner.active ? "Active" : "Inactive"}
                </Badge>
                <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)} data-testid="edit-practitioner-button">
                  Edit
                </Button>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Commitments</CardTitle>
                  <Button size="sm" variant="secondary" onClick={() => setCommitmentOpen(true)} data-testid="start-commitment-button">
                    Start commitment
                  </Button>
                </CardHeader>
                <CardContent>
                  {!commitments || commitments.length === 0 ? (
                    <p className="text-sm text-text-muted">No commitments on file.</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {commitments.map((c) => (
                        <CommitmentRow key={c.id} commitment={c} />
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Certificate of Insurance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm text-text-secondary">
                  <p>Provider: {practitioner.coi_provider_name ?? "—"}</p>
                  <p>Policy number: {practitioner.coi_policy_number ?? "—"}</p>
                  <p>Expiry: {practitioner.coi_expiry_date ?? "—"}</p>
                  {practitioner.notes && <p className="pt-2 text-text-muted">Notes: {practitioner.notes}</p>}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Booking history</CardTitle>
                  <Button size="sm" onClick={() => setBookOpen(true)} data-testid="book-session-button">
                    Book a session
                  </Button>
                </CardHeader>
                <CardContent>
                  {!bookings || bookings.length === 0 ? (
                    <p className="text-sm text-text-muted">No sessions booked yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="practitioner-bookings-table">
                        <thead>
                          <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                            <th className="px-2 py-2">Date</th>
                            <th className="px-2 py-2">Room</th>
                            <th className="px-2 py-2">Status</th>
                            <th className="px-2 py-2">Price</th>
                            <th className="px-2 py-2">Invoice</th>
                            <th className="px-2 py-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {bookings.map((b) => (
                            <BookingRow key={b.id} booking={b} practitionerId={practitioner.id} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </QueryStateBoundary>

      {practitioner && (
        <>
          <PractitionerFormDialog open={editOpen} onClose={() => setEditOpen(false)} practitioner={practitioner} />
          <StartCommitmentDialog open={commitmentOpen} onClose={() => setCommitmentOpen(false)} practitionerId={practitioner.id} />
          <BookPractitionerSessionDialog open={bookOpen} onClose={() => setBookOpen(false)} practitionerId={practitioner.id} />
        </>
      )}
    </div>
  );
}

function CommitmentRow({ commitment }: { commitment: PractitionerCommitment }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
      <span className="text-text-primary">{commitment.start_month}</span>
      <span className="text-text-secondary">
        {commitment.target_sessions_per_month} sessions/month x {commitment.target_months} months
      </span>
      <span className="text-text-muted">{commitment.discount_pct}% off</span>
      <Badge variant={COMMITMENT_STATUS_BADGE_VARIANT[commitment.status]}>{commitment.status}</Badge>
    </li>
  );
}

function BookingRow({ booking, practitionerId }: { booking: PractitionerBooking; practitionerId: string }) {
  const confirmBooking = useConfirmPractitionerBooking();
  const cancelBooking = useCancelPractitionerBooking();
  const completeBooking = useCompletePractitionerBooking();
  const generateInvoice = useGeneratePractitionerInvoice();

  async function handleConfirm() {
    try {
      await confirmBooking.mutateAsync(booking.id);
      toastSuccess("Session confirmed");
    } catch (err) {
      toastError("Couldn't confirm session", getErrorMessage(err));
    }
  }

  async function handleCancel() {
    try {
      await cancelBooking.mutateAsync(booking.id);
      toastSuccess("Session cancelled");
    } catch (err) {
      toastError("Couldn't cancel session", getErrorMessage(err));
    }
  }

  async function handleComplete() {
    try {
      await completeBooking.mutateAsync(booking.id);
      toastSuccess("Session marked completed");
    } catch (err) {
      toastError("Couldn't complete session", getErrorMessage(err));
    }
  }

  async function handleGenerateInvoice() {
    try {
      const invoice = await generateInvoice.mutateAsync({ practitionerBookingId: booking.id, practitionerId });
      toastSuccess("Invoice generated", invoice.number);
    } catch (err) {
      toastError("Couldn't generate invoice", getErrorMessage(err));
    }
  }

  return (
    <tr data-testid={`practitioner-booking-row-${booking.id}`}>
      <td className="px-2 py-2 text-text-primary">{booking.booking_date}</td>
      <td className="px-2 py-2 text-text-secondary">{booking.resource_name}</td>
      <td className="px-2 py-2">
        <Badge variant={BOOKING_STATUS_BADGE_VARIANT[booking.status]}>{BOOKING_STATUS_LABELS[booking.status]}</Badge>
      </td>
      <td className="px-2 py-2 text-text-secondary">
        {booking.discount_pct > 0 ? (
          <>
            {centsToUsd(booking.base_price_cents)} → <span className="font-semibold text-text-primary">{centsToUsd(booking.price_cents)}</span>
            {" "}
            ({booking.discount_pct}% off{booking.discount_reason ? ` — ${booking.discount_reason}` : ""})
          </>
        ) : (
          centsToUsd(booking.price_cents)
        )}
      </td>
      <td className="px-2 py-2">
        {booking.invoice_id ? (
          <Badge variant="info">Invoiced</Badge>
        ) : booking.status === "confirmed" || booking.status === "completed" ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void handleGenerateInvoice()}
            disabled={generateInvoice.isPending}
            data-testid={`generate-invoice-${booking.id}`}
          >
            {generateInvoice.isPending ? "Generating…" : "Generate invoice"}
          </Button>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-2">
          {booking.status === "inquiry" && (
            <>
              <Button
                size="sm"
                onClick={() => void handleConfirm()}
                disabled={confirmBooking.isPending}
                data-testid={`confirm-practitioner-booking-${booking.id}`}
              >
                {confirmBooking.isPending ? "Confirming…" : "Confirm"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => void handleCancel()} disabled={cancelBooking.isPending}>
                Cancel
              </Button>
            </>
          )}
          {booking.status === "confirmed" && (
            <>
              <Button size="sm" onClick={() => void handleComplete()} disabled={completeBooking.isPending}>
                {completeBooking.isPending ? "Completing…" : "Mark completed"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => void handleCancel()} disabled={cancelBooking.isPending}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function StartCommitmentDialog({
  open,
  onClose,
  practitionerId,
}: {
  open: boolean;
  onClose: () => void;
  practitionerId: string;
}) {
  const [startMonth, setStartMonth] = useState("");
  const createCommitment = useCreateCommitment();

  useEffect(() => {
    if (open) setStartMonth("");
  }, [open]);

  const canSubmit = !!startMonth && !createCommitment.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createCommitment.mutateAsync({ practitionerId, start_month: startMonth });
      toastSuccess("Commitment started");
      onClose();
    } catch (err) {
      toastError("Couldn't start commitment", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Start commitment">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Start commitment</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Start month"
            type="date"
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
            hint="4 sessions/month for 3 months running earns 30% off — a broken streak never claws back discounts already given."
            data-testid="commitment-start-month-input"
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createCommitment.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-commitment-form">
            {createCommitment.isPending ? "Starting…" : "Start commitment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
